import { codeWords, daysUntil, shortDate, type Assessment, type Vendor } from '../../lib/api'
import { FINISHED, WAITING_ON_US, WITH_VENDOR as WITH_VENDOR_STATES, questionnaireWord, statusWord, type StatusWord } from '../../lib/status'
import type { VendorExitRecord } from '../../lib/exit'
import type { Tone } from '../kit'

// The small readings every part of the vendor record shares: the lifecycle as a
// word, where the questionnaire has got to, the worst finding, what data the
// vendor holds. Each one is computed from what the API returned and nothing
// else, so the header, the highlights strip and the overview cannot disagree
// with each other, and none of them can say something the record does not.

export function vendorName(v: Vendor): string {
  return v.displayName?.trim() || v.legalName
}

// The vendor's lifecycle as a word, from the one vocabulary (lib/status.ts).
// Callers that know whether anything has been sent use vendorStage there, so a
// vendor not yet approved reads Requested or In due diligence, never "Draft".
export function lifecycleWord(status: string): { word: string; tone: Tone } {
  const s = statusWord('vendor', status)
  return { word: s.word, tone: s.tone }
}

// The exit that is live on the vendor, proposed or under way.
export function liveExitOf(exits: VendorExitRecord[] | undefined): VendorExitRecord | null {
  return exits?.find((e) => e.status === 'PROPOSED' || e.status === 'OPEN') ?? null
}

// The questionnaire sets, from the one vocabulary. OPEN_STATES is the narrower
// set whose invitation can be sent again (the API refuses a returned one).
export const OPEN_STATES = new Set(['ISSUED', 'IN_PROGRESS'])
export const WITH_VENDOR = WITH_VENDOR_STATES
export const REVIEW_STATES = WAITING_ON_US
export const SCORED_STATES = FINISHED

export type QuestionnaireReading = {
  // One or two words, from the vocabulary.
  word: string
  tone: Tone
  icon: StatusWord['icon']
  // One short line under it: the reference and the date that matters.
  line: string
  assessment: Assessment | null
  // Days past due, only for one still with the vendor.
  lateBy: number | null
}

function newest(a: Assessment, b: Assessment): number {
  return (b.submittedAt ?? b.issuedAt ?? '').localeCompare(a.submittedAt ?? a.issuedAt ?? '')
}

function reading(s: StatusWord, line: string, assessment: Assessment | null, lateBy: number | null): QuestionnaireReading {
  return { word: s.word, tone: s.tone, icon: s.icon, line, assessment, lateBy }
}

// Where the vendor's questionnaire has got to. Answers waiting for review come
// first because they wait on us; then one still with the vendor, late or not;
// then the newest one on record.
export function questionnaireReading(assessments: Assessment[]): QuestionnaireReading {
  const review = assessments.find((a) => WAITING_ON_US.has(a.status))
  if (review) {
    return reading(statusWord('questionnaire', review.status), review.reference + (review.submittedAt ? ', ' + shortDate(review.submittedAt) : ''), review, null)
  }
  const out = assessments.find((a) => WITH_VENDOR_STATES.has(a.status))
  if (out) {
    const d = daysUntil(out.dueOn)
    if (d != null && d < 0) {
      return reading(questionnaireWord(out.status, true), out.reference + ', ' + Math.abs(d) + (Math.abs(d) === 1 ? ' day' : ' days'), out, Math.abs(d))
    }
    return reading(statusWord('questionnaire', out.status), out.reference + (out.dueOn ? ', due ' + shortDate(out.dueOn) : ''), out, null)
  }
  const latest = [...assessments].sort(newest)[0] ?? null
  if (!latest) return reading(statusWord('questionnaire', 'NOT_SENT'), 'None on record', null, null)
  return reading(statusWord('questionnaire', latest.status), latest.reference, latest, null)
}

const SEVERITY_RANK: Record<string, number> = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 }

export function severityTone(severity: string): Tone {
  switch (severity) {
    case 'CRITICAL':
      return 'crit'
    case 'HIGH':
      return 'warn'
    case 'MEDIUM':
      return 'info'
    default:
      return 'neutral'
  }
}

export type FindingsReading = {
  standing: number
  // The worst severity among the standing findings, or null when it cannot be
  // known from the page the record read carries.
  worst: string | null
  bySeverity: Record<string, number>
  overdue: number | null
  // True when the inline page holds every standing finding, so counts by
  // severity are the whole answer and not the first fifty.
  complete: boolean
}

// The record read carries at most fifty standing findings, most overdue first.
// When there are more, a count by severity would be a count of the first page,
// so the worst severity is claimed only if it is Critical (nothing can be worse)
// or the page is complete.
export function findingsReading(v: Vendor): FindingsReading {
  const items = v.findings?.items ?? []
  const standing = v.findings?.standing ?? v.standingFindings ?? 0
  const complete = items.length >= standing
  const bySeverity: Record<string, number> = {}
  let worst: string | null = null
  let overdue = 0
  for (const f of items) {
    bySeverity[f.severity] = (bySeverity[f.severity] ?? 0) + 1
    if (!worst || (SEVERITY_RANK[f.severity] ?? 0) > (SEVERITY_RANK[worst] ?? 0)) worst = f.severity
    if (f.ageingDays > 0) overdue += 1
  }
  if (!complete && worst !== 'CRITICAL') worst = null
  return { standing, worst: standing > 0 ? worst : null, bySeverity, overdue: complete ? overdue : null, complete }
}

// A finding raised because a material vendor has no current exit plan. The API
// marks it with an observation key the read does not carry, so it is known by
// its source and the words the server gives it.
export function isExitPlanFinding(f: { source: string; title: string }): boolean {
  return f.source === 'OBSERVED' && /exit plan/i.test(f.title)
}

// What the vendor handles, as two or three words, and where it is kept. The
// processing record wins where one exists, because it names the classification
// and the countries; the profile's categories are the fallback.
export function dataHandled(v: Vendor): { words: string; full: string; line: string } {
  const dp = v.dataProcessing
  const cats = (v.dataCategories ?? []).map((c) => codeWords(c))
  const full = cats.join(', ')
  // The classification is the one word that sets the obligations, so it leads
  // when the processing record has one; the categories are in the title.
  const words = dp?.classification ? codeWords(dp.classification) : cats.slice(0, 2).join(', ') + (cats.length > 2 ? ' +' + (cats.length - 2) : '')
  let line = ''
  if (dp) {
    const abroad =
      (dp.transferCountries?.length ?? 0) > 0 ||
      (Boolean(dp.storageCountry) && dp.storageCountry !== 'IN') ||
      (Boolean(dp.processingCountry) && dp.processingCountry !== 'IN')
    line = abroad ? 'Leaves India' : 'Stays in India'
  } else if (v.crossBorder) {
    line = 'Cross border declared'
  }
  return { words: words || 'None declared', full: full || 'None declared', line }
}

// A date the way the record shows dates. Some reads return Postgres timestamps
// ("2026-09-08 10:53:25+00"), which the browser's parser refuses, so the space
// and the short offset are put into the form it accepts before giving up.
export function dayOf(iso: string | null | undefined): string {
  if (!iso) return ''
  const direct = shortDate(iso)
  if (!/^\d{4}-\d{2}-\d{2}/.test(direct)) return direct
  const fixed = iso.trim().replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00')
  return shortDate(fixed)
}

export function daysWord(n: number): string {
  return Math.abs(n) + (Math.abs(n) === 1 ? ' day' : ' days')
}
